import React from 'react';
import { Avatar, Box, Collapse, IconButton, Link, Paper, TableCell, TableRow, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, TrendingDown, VisibilityOutlined } from '@mui/icons-material';
import BalanceTrack from '../BalanceTrack';
import BrandDepth from './BrandDepth';
import BrandTiming from './BrandTiming';
import { numCellSx } from './listColumns';
import { BALANCE_TOOLTIP } from './constants';
import { balanceLabel, formatStatLine, stillWantedSignal, thinnerSignal } from '../../../utils/buyerInsightsFormat';

const Initial = ({ brand }) => (
  <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: '#eceff1', color: 'text.primary' }}>
    {(brand || '?').charAt(0).toUpperCase()}
  </Avatar>
);

const signalSx = { display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', textAlign: 'left' };

// Brand name (a link into Products) and the inline signals under it (spec §1.3): "Thinner in …"
// (orange, a stock problem — opens the row's breakdown) and "… sizes still wanted" (grey, demand).
const NameAndSignals = ({ row, onOpen, onExpand }) => (
  <Box sx={{ minWidth: 0 }}>
    <Link
      component="button"
      variant="body2"
      underline="hover"
      onClick={(e) => {
        e.stopPropagation();
        onOpen({ view: 'all' }, 'brand_name');
      }}
      sx={{ fontWeight: 500, textAlign: 'left', color: 'text.primary', '&:hover': { color: 'primary.main' } }}
    >
      {row.brand}
    </Link>
    {thinnerSignal(row.thinner) && (
      <Box>
        <Link
          component="button"
          variant="caption"
          underline="hover"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          sx={{ ...signalSx, color: 'warning.dark' }}
        >
          <TrendingDown sx={{ fontSize: 14 }} />
          {thinnerSignal(row.thinner)}
        </Link>
      </Box>
    )}
    {row.still_wanted_sizes > 0 && (
      <Box>
        <Link
          component="button"
          variant="caption"
          underline="hover"
          color="text.secondary"
          onClick={(e) => {
            e.stopPropagation();
            onOpen({ view: 'still_wanted' }, 'still_wanted_signal');
          }}
          sx={signalSx}
        >
          <VisibilityOutlined sx={{ fontSize: 14 }} />
          {stillWantedSignal(row.still_wanted_sizes, row.still_wanted_products)}
        </Link>
      </Box>
    )}
  </Box>
);

const Balance = ({ row }) => (
  <Box>
    <BalanceTrack pos={row.balance.pos} tooltip={BALANCE_TOOLTIP} />
    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {balanceLabel(row.balance)}
    </Typography>
  </Box>
);

// Expanded brand (spec §1.4): By size → By colour → Timing → still wanted.
const Expanded = ({ row, detailParams, window, isWide, open }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
    <BrandDepth brand={row.brand} params={detailParams} window={window} isWide={isWide} />
    <Paper variant="outlined" sx={{ px: 2, py: 0.5 }}>
      <BrandTiming row={row} onOpen={open} />
    </Paper>
  </Box>
);

/** One carried brand: wide = table row + collapsible breakdown; narrow = card. */
const BrandRow = ({ row, isWide, expanded, onToggle, onOpen, detailParams, window, columns = [] }) => {
  const open = (initial, source) => onOpen({ ...initial, brand: [row.brand] }, source);
  const expand = () => {
    if (!expanded) onToggle(row.brand);
  };
  const chevron = (
    <IconButton size="small" onClick={() => onToggle(row.brand)} aria-label={expanded ? 'Collapse' : 'Expand'}>
      {expanded ? <ExpandLess /> : <ExpandMore />}
    </IconButton>
  );
  if (isWide) {
    return (
      <>
        <TableRow hover sx={{ '& > td': { borderBottom: expanded ? 'none' : undefined } }}>
          <TableCell sx={{ width: 40, px: 0.5 }}>{chevron}</TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Initial brand={row.brand} />
              <NameAndSignals row={row} onOpen={open} onExpand={expand} />
            </Box>
          </TableCell>
          <TableCell>
            <Balance row={row} />
          </TableCell>
          {columns.map((c) => (
            <TableCell key={c.key} align="right" sx={numCellSx(c)}>
              {c.fmt(c.get(row))}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell colSpan={3 + columns.length} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ pl: { xs: 0, md: 7 }, pr: 1 }}>
                <Expanded row={row} detailParams={detailParams} window={window} isWide={isWide} open={open} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Initial brand={row.brand} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <NameAndSignals row={row} onOpen={open} onExpand={expand} />
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2">{formatStatLine('received', row.received)}</Typography>
            <Typography variant="body2">{formatStatLine('sent', row.sent)}</Typography>
          </Box>
          <Box sx={{ mt: 0.75 }}>
            <Balance row={row} />
          </Box>
        </Box>
        {chevron}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Expanded row={row} detailParams={detailParams} window={window} isWide={isWide} open={open} />
      </Collapse>
    </Paper>
  );
};

export default BrandRow;
